import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import { getAgencyRequisites, setAgencyRequisites } from "../../../redux/user/actions";
import { userSelectors } from "../../../redux/user/selectors";
import { DOMAIN_API_HOST } from "../../../configs/main";
import useAgencyUser from "../../../data/useAgencyUser";
import "./AgencyRequisites.scss";

const CreateBankAuditSchema = Yup.object().shape({
  full_name: Yup.string().max(300).required("Обязательное поле"),
  short_name: Yup.string().max(300).required("Обязательное поле"),
  ur_address: Yup.string().max(300).required("Обязательное поле"),
  mail_address: Yup.string().max(300).required("Обязательное поле"),
  inn_or_kpp: Yup.string()
    .matches(/^(\d{10}|\d{12})\/(\d{9})?$/)
    .test("inn_or_kpp", "1", function (value) {
      const orgnLength = this.parent.orgn ? this.parent.orgn.length : 0;
      const innLength = value ? value.split("/")[0].length : 0;
      return (
        (innLength === 12 && orgnLength === 15) ||
        (innLength === 10 && orgnLength === 13)
      );
    })
    .required("Обязательное поле"),
  orgn: Yup.string()
    .matches(/^(\d{13}|\d{15})$/)
    .test("orgn", "1", function (value) {
      const innLength = this.parent.inn_or_kpp ? this.parent.inn_or_kpp.split("/")[0].length : 0;
      const orgnLength = value ? value.length : 0;
      return (
        (innLength === 10 && orgnLength === 13) ||
        (innLength === 12 && orgnLength === 15)
      );
    })
    .required("Обязательное поле"),
  director: Yup.string().max(300).required("Обязательное поле"),
  bank_name: Yup.string().max(300).required("Обязательное поле"),
  bank_bik: Yup.string().matches(/\d{9}/).length(9).required("Обязательное поле"),
  correspondent_account: Yup.string().matches(/\d{20}/).length(20).required("Обязательное поле"),
  payment_account: Yup.string().matches(/\d{20}/).length(20).required("Обязательное поле"),
  okvad: Yup.string().max(9).matches(/^[0-9.]*$/).required("Обязательное поле"),
});

function AgencyRequisites() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { role } = useAgencyUser();

  const {
    full_name,
    short_name,
    ur_address,
    mail_address,
    inn_or_kpp,
    orgn,
    director,
    bank_name,
    bank_bik,
    correspondent_account,
    payment_account,
    okvad,
    user,
  } = useSelector(userSelectors.all_assign_properties);

  useEffect(() => {
    dispatch(getAgencyRequisites());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      full_name: full_name || "",
      short_name: short_name || "",
      ur_address: ur_address || "",
      mail_address: mail_address || "",
      inn_or_kpp: inn_or_kpp || "",
      orgn: orgn || "",
      director: director || "",
      bank_name: bank_name || "",
      bank_bik: bank_bik || "",
      correspondent_account: correspondent_account || "",
      payment_account: payment_account || "",
      okvad: okvad || "",
    },
    enableReinitialize: true,
    validationSchema: CreateBankAuditSchema,
    onSubmit: (values) => {
      if (full_name && !["ADMIN", "SUPERUSER"].some((el) => el === role)) {
        enqueueSnackbar("Данные нельзя редактировать. Свяжитесь с поддержкой", {
          variant: "error",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      } else {
        if (!["MANAGER", "ADMIN", "SUPERUSER"].some((el) => el === role)) {
          const result = window.confirm(
            "Убедитесь, что все поля заполнены верно. Вы больше не сможете редактировать банковские реквизиты"
          );
          if (!result) {
            return;
          }
        }
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Authorization", "Bearer " + localStorage.getItem("agency_token"));
        fetch(`${DOMAIN_API_HOST}/api/v1/me/requisites`, {
          method: "PUT",
          headers: myHeaders,
          body: JSON.stringify({
            ...values,
            inn_or_kpp: values.inn_or_kpp ? values.inn_or_kpp : 0,
            orgn: values.orgn ? values.orgn : 0,
            bank_bik: values.bank_bik ? values.bank_bik : 0,
            correspondent_account: values.correspondent_account ? values.correspondent_account : 0,
            payment_account: values.payment_account ? values.payment_account : 0,
          }),
        })
          .then((res) => {
            if (res.ok) {
              return res.json();
            } else {
              throw new Error("Ошибка при сохранении");
            }
          })
          .then((data) => {
            dispatch(setAgencyRequisites(data));
            enqueueSnackbar("Информация сохранена", {
              variant: "success",
              autoHideDuration: 3000,
              anchorOrigin: { vertical: "top", horizontal: "right" },
            });
          })
          .catch((error) => {
            enqueueSnackbar(error.message || "Ошибка", {
              variant: "error",
              autoHideDuration: 3000,
              anchorOrigin: { vertical: "top", horizontal: "right" },
            });
          });
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, submitCount } = formik;

  const shouldShowError = (fieldName) => {
    const isTouched = touched[fieldName];
    const hasError = !!errors[fieldName];
    const fieldValue = values[fieldName];
    const isEmpty = !fieldValue || fieldValue.trim() === "";

    if (!hasError) return false;
    if (submitCount > 0) return true;
    if (!isTouched) return false;
    return !isEmpty;
  };

  if (!user) {
    return (
      <div className="AgencyRequisites">
        <p>Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="AgencyRequisites">
      <h2>Реквизиты</h2>
      <form onSubmit={handleSubmit} className="AgencyRequisites-form">
        <section>
          <h3>Общие данные</h3>
          <div className="AgencyRequisites-grid">
            <FormControl className="input-data">
              <TextField
                name="full_name"
                label="Полное наименование"
                value={values.full_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={shouldShowError("full_name")}
                helperText={shouldShowError("full_name") ? errors.full_name : values.full_name?.length > 300 ? "Значение должно быть короче 300 символов" : ""}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
            <FormControl className="input-data">
              <TextField
                name="short_name"
                label="Сокращенное наименование"
                value={values.short_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={shouldShowError("short_name")}
                helperText={shouldShowError("short_name") ? errors.short_name : values.short_name?.length > 300 ? "Значение должно быть короче 300 символов" : ""}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
            <FormControl className="input-data">
              <TextField
                name="ur_address"
                label="Юридический адрес"
                value={values.ur_address}
                onChange={handleChange}
                onBlur={handleBlur}
                error={shouldShowError("ur_address")}
                helperText={shouldShowError("ur_address") ? errors.ur_address : values.ur_address?.length > 300 ? "Значение должно быть короче 300 символов" : ""}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
            <FormControl className="input-data">
              <TextField
                name="mail_address"
                label="Почтовый адрес"
                value={values.mail_address}
                onChange={handleChange}
                onBlur={handleBlur}
                error={shouldShowError("mail_address")}
                helperText={shouldShowError("mail_address") ? errors.mail_address : values.mail_address?.length > 300 ? "Значение должно быть короче 300 символов" : ""}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </div>
        </section>

        <section>
          <h3>Банковские реквизиты</h3>
          <div className="AgencyRequisites-grid">
            <FormControl className="input-data">
              <TextField
                name="inn_or_kpp"
                label="ИНН / КПП"
                value={values.inn_or_kpp}
                onChange={handleChange}
                onBlur={handleBlur}
                error={shouldShowError("inn_or_kpp")}
                helperText={shouldShowError("inn_or_kpp") && errors.inn_or_kpp === "1" ? "Неверная комбинация ИНН и ОГРН." : shouldShowError("inn_or_kpp") ? "Значение ИНН и КПП через слэш" : ""}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
            <FormControl className="input-data">
              <TextField
                name="orgn"
                label="ОГРН"
                value={values.orgn}
                onChange={handleChange}
                onBlur={handleBlur}
                error={shouldShowError("orgn")}
                helperText={shouldShowError("orgn") && errors.orgn === "1" ? "Неверная комбинация ОГРН и ИНН." : shouldShowError("orgn") ? "Значение должно быть ОГРН" : ""}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
            <FormControl className="input-data">
              <TextField
                name="director"
                label="Директор"
                value={values.director}
                onChange={handleChange}
                onBlur={handleBlur}
                error={shouldShowError("director")}
                helperText={shouldShowError("director") ? errors.director : values.director?.length > 300 ? "Значение должно быть короче 300 символов" : ""}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
            <FormControl className="input-data">
              <TextField
                name="bank_name"
                label="Наименование банка"
                value={values.bank_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={shouldShowError("bank_name")}
                helperText={shouldShowError("bank_name") ? errors.bank_name : values.bank_name?.length > 300 ? "Значение должно быть короче 300 символов" : ""}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
            <FormControl className="input-data">
              <TextField
                name="bank_bik"
                label="БИК"
                value={values.bank_bik}
                onChange={handleChange}
                onBlur={handleBlur}
                error={shouldShowError("bank_bik")}
                helperText={shouldShowError("bank_bik") ? "Значение должно быть БИК" : (values.bank_bik !== '' && values.bank_bik?.length > 9) ? "Значение должно быть ровно 9 символов" : ""}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
            <FormControl className="input-data">
              <TextField
                name="correspondent_account"
                label="Корреспондентский счёт"
                value={values.correspondent_account}
                onChange={handleChange}
                onBlur={handleBlur}
                error={shouldShowError("correspondent_account")}
                helperText={shouldShowError("correspondent_account") ? "Значение должно быть корреспондентский счет" : (values.correspondent_account !== '' && values.correspondent_account?.length > 20) ? "Значение должно быть ровно 20 символов" : ""}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
            <FormControl className="input-data">
              <TextField
                name="payment_account"
                label="Расчетный счёт"
                value={values.payment_account}
                onChange={handleChange}
                onBlur={handleBlur}
                error={shouldShowError("payment_account")}
                helperText={shouldShowError("payment_account") ? "Значение должно быть расчетный счет" : (values.payment_account !== '' && values.payment_account?.length > 20) ? "Значение должно быть ровно 20 символов" : ""}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
            <FormControl className="input-data">
              <TextField
                name="okvad"
                label="ОКВЭД"
                value={values.okvad}
                onChange={handleChange}
                onBlur={handleBlur}
                error={shouldShowError("okvad")}
                helperText={values.okvad?.length > 9 ? "Значение должно быть короче 9 символов" : shouldShowError("okvad") ? "Значение должно быть ОКВЭД" : ""}
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </div>
        </section>

        <div className="AgencyRequisites-actions">
          <button type="submit">Сохранить</button>
        </div>
      </form>
    </div>
  );
}

export default AgencyRequisites;

